import { z } from 'zod';

// 上下文类型
export enum ContextType {
  SYSTEM = 'system',          // 系统提示词
  USER = 'user',              // 用户输入
  HISTORY = 'history',        // 历史对话
  KNOWLEDGE = 'knowledge',    // 知识库
  WORKFLOW = 'workflow',      // 工作流
  TOOL = 'tool',              // 工具结果
  STATE = 'state',            // 状态信息
  METADATA = 'metadata'       // 元数据
}

// 上下文项
export interface ContextItem {
  id: string;
  type: ContextType;
  content: any;
  priority: number;           // 0-100，越高越重要
  relevance?: number;         // 0-1，相关性分数
  size?: number;              // 字节大小
  timestamp: number;
  source?: string;
  metadata?: Record<string, any>;
}

// 上下文配置
export interface ContextConfig {
  maxSize?: number;           // 最大上下文大小（字节）
  maxItems?: number;          // 最大项目数
  compressionThreshold?: number; // 压缩阈值
  relevanceThreshold?: number;   // 相关性阈值
  cacheTTL?: number;          // 缓存生存时间（毫秒）
  enableCompression?: boolean;
  enableCache?: boolean;
  enableOptimization?: boolean;
}

// 上下文
export interface Context {
  id: string;
  items: ContextItem[];
  totalSize: number;
  metadata: {
    created: number;
    updated: number;
    compressed: boolean;
    optimized: boolean;
    [key: string]: any;
  };
}

// 上下文查询
export interface ContextQuery {
  types?: ContextType[];
  sources?: string[];
  minRelevance?: number;
  maxAge?: number;            // 毫秒
  keywords?: string[];
  limit?: number;
  offset?: number;
}

// 上下文优化策略
export enum OptimizationStrategy {
  RELEVANCE = 'relevance',    // 基于相关性
  RECENCY = 'recency',        // 基于时间
  PRIORITY = 'priority',      // 基于优先级
  SIZE = 'size',              // 基于大小
  HYBRID = 'hybrid'           // 混合策略
}

// 上下文压缩策略
export enum CompressionStrategy {
  SUMMARY = 'summary',        // 摘要
  TRUNCATE = 'truncate',      // 截断
  SAMPLE = 'sample',          // 采样
  SEMANTIC = 'semantic'       // 语义压缩
}

// 工具调用上下文
export interface ToolContext {
  toolName: string;
  parameters: any;
  result?: any;
  error?: Error;
  duration?: number;
  timestamp: number;
}

// 知识项
export interface KnowledgeItem {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
  embedding?: number[];       // 向量嵌入
  score?: number;             // 匹配分数
  metadata?: Record<string, any>;
}

// 工作流上下文
export interface WorkflowContext {
  id: string;
  name: string;
  description: string;
  steps: Array<{
    id: string;
    name: string;
    description: string;
    completed: boolean;
    result?: any;
  }>;
  variables: Record<string, any>;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

// 上下文模板
export interface ContextTemplate {
  id: string;
  name: string;
  description: string;
  items: Array<{
    type: ContextType;
    template: string;
    variables?: string[];
    required?: boolean;
  }>;
  metadata?: Record<string, any>;
}

// 上下文元数据
export interface ContextMetadata {
  created: number;
  updated: number;
  compressed: boolean;
  optimized: boolean;
  [key: string]: any;
}

// 历史项
export interface HistoryItem {
  id: string;
  type: string;
  content: any;
  timestamp: number;
  metadata?: Record<string, any>;
}

// 优化选项
export interface OptimizationOptions {
  strategy: OptimizationStrategy;
  maxSize?: number;
  maxItems?: number;
  relevanceThreshold?: number;
  preserveTypes?: ContextType[];
}

// 优化结果
export interface OptimizationResult {
  originalSize: number;
  optimizedSize: number;
  removedItems: number;
  duration: number;
  strategy: OptimizationStrategy;
}

// 压缩选项
export interface CompressionOptions {
  strategy: CompressionStrategy;
  targetSize?: number;
  preserveStructure?: boolean;
  summaryLength?: number;
}

// 压缩结果
export interface CompressionResult {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  duration: number;
  strategy: CompressionStrategy;
}

// Schema 定义
export const contextItemSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(ContextType),
  content: z.any(),
  priority: z.number().min(0).max(100),
  relevance: z.number().min(0).max(1).optional(),
  size: z.number().optional(),
  timestamp: z.number(),
  source: z.string().optional(),
  metadata: z.record(z.any()).optional()
});

export const contextConfigSchema = z.object({
  maxSize: z.number().optional(),
  maxItems: z.number().optional(),
  compressionThreshold: z.number().optional(),
  relevanceThreshold: z.number().optional(),
  cacheTTL: z.number().optional(),
  enableCompression: z.boolean().optional(),
  enableCache: z.boolean().optional(),
  enableOptimization: z.boolean().optional()
});

export const contextSchema = z.object({
  id: z.string(),
  items: z.array(contextItemSchema),
  totalSize: z.number(),
  metadata: z.object({
    created: z.number(),
    updated: z.number(),
    compressed: z.boolean(),
    optimized: z.boolean()
  }).passthrough()
});